import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  kostenItems as fallbackItems,
  kostenDisclaimer as fallbackDisclaimer,
  kostenFaq as fallbackFaq,
} from "@/lib/kosten-content";

export interface KostenFaqEntry {
  vraag: string;
  antwoord: string;
}

export interface KostenContent {
  items: {
    id: string | null;
    slug: string;
    title: string;
    prijs: string | null;
    prijsNoot: string | null;
    factoren: string[];
    sortOrder: number;
  }[];
  disclaimer: string;
  faq: KostenFaqEntry[];
}

const fallbackContent: KostenContent = {
  items: fallbackItems.map((item, index) => ({
    id: null,
    slug: item.slug,
    title: item.title,
    prijs: item.prijs,
    prijsNoot: item.prijsNoot ?? null,
    factoren: item.factoren,
    sortOrder: (index + 1) * 10,
  })),
  disclaimer: fallbackDisclaimer,
  faq: fallbackFaq,
};

const faqSchema = z.array(
  z.object({ vraag: z.string().min(1).max(300), antwoord: z.string().min(1).max(3000) }),
);

const itemSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, "Alleen kleine letters, cijfers en streepjes"),
  title: z.string().min(1).max(120),
  prijs: z.string().max(120).nullable(),
  prijs_noot: z.string().max(600).nullable(),
  factoren: z.array(z.string().min(1).max(240)).max(12),
  sort_order: z.number().int().min(0).max(10000),
});

/** Publiek leesbare kosteninhoud; valt terug op de code-inhoud als de database leeg is.
 * De tabellen zijn niet meer direct publiek leesbaar: de server leest ze met de
 * afgeschermde serverclient en geeft alleen deze specifieke tekst terug. */
export const getKostenContent = createServerFn({ method: "GET" }).handler(
  async (): Promise<KostenContent> => {
    if (!process.env["SUPABASE_URL"] || !process.env["SUPABASE_SERVICE_ROLE_KEY"]) {
      return fallbackContent;
    }

    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const [itemsRes, settingsRes] = await Promise.all([
        supabaseAdmin
          .from("kosten_items")
          .select("id, slug, title, prijs, prijs_noot, factoren, sort_order")
          .order("sort_order", { ascending: true }),
        supabaseAdmin.from("kosten_settings").select("disclaimer, faq").maybeSingle(),
      ]);

      const rows = itemsRes.data ?? [];
      if (itemsRes.error || rows.length === 0) return fallbackContent;

      const parsedFaq = faqSchema.safeParse(settingsRes.data?.faq ?? []);

      return {
        items: rows.map((row: (typeof rows)[number]) => ({
          id: row.id,
          slug: row.slug,
          title: row.title,
          prijs: row.prijs,
          prijsNoot: row.prijs_noot,
          factoren: row.factoren ?? [],
          sortOrder: row.sort_order,
        })),
        disclaimer: settingsRes.data?.disclaimer?.trim()
          ? settingsRes.data.disclaimer
          : fallbackDisclaimer,
        faq: parsedFaq.success && parsedFaq.data.length > 0 ? parsedFaq.data : fallbackFaq,
      };
    } catch {
      return fallbackContent;
    }
  },
);

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Response("Forbidden", { status: 403 });
}

export const saveKostenItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => itemSchema.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { id, ...values } = data;
    if (id) {
      const { error } = await context.supabase
        .from("kosten_items")
        .update(values as never)
        .eq("id", id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await context.supabase.from("kosten_items").insert(values as never);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deleteKostenItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { error } = await context.supabase.from("kosten_items").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const saveKostenSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ disclaimer: z.string().max(1000), faq: faqSchema.max(20) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { error } = await context.supabase
      .from("kosten_settings")
      .upsert({ id: true, disclaimer: data.disclaimer, faq: data.faq } as never)
      .eq("id", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
